/**
 * Encounters API
 * Fetches paginated encounters (orders + SOAP notes) for a patient
 *
 * EMRv2 asks for the list view (view=list): each order's userLabs / userProcedures /
 * userdispensed / userSupplements only hold the items the list reads (checked labs,
 * procedures with a quantity, selection or check, dispensed and supplements with a
 * quantity, and custom / redrawn / noted items). Anything that opens or edits an order
 * must load it whole with GET /orderget/:id first. The old EMR calls the endpoint
 * without the view and still gets whole orders.
 */

import { onApiMutation } from '@/utils/apiMutationListener';
import { apiClient } from './config';

const LIST_VIEW = 'list';

// ── Prefetch by Customer ID ──────────────────────────────────────────────────
// A patient opened from a link (/emr?patientId=<Customer ID>) is only known by its
// Customer ID until the profile arrives. prefetchEncountersByCustomer starts the first
// page right away, alongside the profile; the first matching fetchEncounters call then
// uses that response instead of asking again. Any write this tab sends drops the
// prefetched pages: they may have been read before it.
const PREFETCH_TTL_MS = 30 * 1000;
const PREFETCH_PARAMS = { page: 1, limit: 10, type: 'All' };
const prefetchedByCustomer = new Map(); // customerId -> { promise, startedAt }
let listeningForWrites = false;

export const prefetchEncountersByCustomer = (customerId) => {
  if (!customerId) return;
  if (!listeningForWrites) {
    listeningForWrites = true;
    onApiMutation(() => prefetchedByCustomer.clear());
  }
  const key = String(customerId);
  const existing = prefetchedByCustomer.get(key);
  if (existing && Date.now() - existing.startedAt < PREFETCH_TTL_MS) return;
  const promise = apiClient
    .get(`/get-all-encounters/by-customer/${encodeURIComponent(key)}`, {
      params: { ...PREFETCH_PARAMS, view: LIST_VIEW },
    })
    .then((response) => response.data);
  // A failed prefetch is simply not used; the regular fetch runs instead.
  promise.catch(() => {});
  prefetchedByCustomer.set(key, { promise, startedAt: Date.now() });
};

// Returns the prefetched first page for this patient, or null. Used at most once.
const takePrefetched = async ({ patientId, customerId, page, limit, type, timing }) => {
  const key = String(customerId);
  const entry = prefetchedByCustomer.get(key);
  if (!entry) return null;
  prefetchedByCustomer.delete(key);
  if (Date.now() - entry.startedAt >= PREFETCH_TTL_MS) return null;
  if (page !== PREFETCH_PARAMS.page || limit !== PREFETCH_PARAMS.limit || type !== PREFETCH_PARAMS.type) return null;
  try {
    const { patientId: resolvedPatientId, ...data } = await entry.promise;
    if (String(resolvedPatientId) !== String(patientId)) return null;
    if (timing) timing.requestedAt = entry.startedAt;
    return data;
  } catch {
    return null;
  }
};

export const clearPrefetchedEncounters = () => prefetchedByCustomer.clear();

/**
 * Fetch a page of encounters for a patient
 * @param {string} patientId   - MongoDB _id
 * @param {string} customerId  - Customer ID (string key used by Orders collection)
 * @param {number} page        - 1-based page number
 * @param {number} limit       - Items per page
 * @param {string} type        - "All" | "Treatments" | "Orders" | "SOAP"
 * @param {object} [timing]    - Receives requestedAt: when the request that produced the
 *                               answer was sent (for a prefetched page, when the prefetch was)
 * @returns {{ data: object[], total: number, page: number, totalPages: number, version?: string }}
 */
export const fetchEncounters = async ({ patientId, customerId, page = 1, limit = 10, type = 'All', timing }) => {
  const prefetched = await takePrefetched({ patientId, customerId, page, limit, type, timing });
  if (prefetched) return prefetched;

  if (timing) timing.requestedAt = Date.now();
  const response = await apiClient.get(
    `/get-all-encounters/${patientId}/${customerId}`,
    { params: { page, limit, type, view: LIST_VIEW } }
  );
  return response.data;
};

/**
 * A fingerprint of everything the encounters list shows. When it matches the one the
 * cached list was loaded with, the cached list is still current.
 * @returns {Promise<string>}
 */
export const fetchEncountersVersion = async ({ patientId, customerId }) => {
  const response = await apiClient.get(`/get-all-encounters/${patientId}/${customerId}/version`);
  return response.data?.version;
};

/**
 * The whole order, for popups and copy: list rows only carry the touched catalog items.
 * Returns the row with its four arrays replaced by the full ones; every other field is
 * left as the list had it, so popups receive the same shape as before.
 */
export const ORDER_ITEM_ARRAYS = ['userLabs', 'userProcedures', 'userdispensed', 'userSupplements'];

export const fetchFullOrderItems = async (orderId) => {
  const response = await apiClient.get(`/orderget/${orderId}`);
  const order = response.data;
  if (!order || !order._id) throw new Error('Order not found');
  return order;
};

export const withFullOrderItems = (row, fullOrder) => {
  const next = { ...row };
  for (const key of ORDER_ITEM_ARRAYS) {
    if (Object.prototype.hasOwnProperty.call(fullOrder, key)) next[key] = fullOrder[key];
    else delete next[key];
  }
  return next;
};
